import React, { useState } from 'react';
import { Box, Container, Flex, Text, Heading } from 'theme-ui';

import { Section, Link, Picture } from '@snippets';
import { withInView } from '@utils';

import { Schema } from './Timeline.schema';
import { themed } from './Timeline.theme';

const DEFAULT_ACCENT = '#c8f500';

export const Timeline = withInView(
  themed(({ theme, cms }) => {
    const { section, header, callouts } = cms;
    const [activeIdx, setActiveIdx] = useState(0);

    if (!callouts?.length) return null;

    const activeCallout = activeIdx !== null ? callouts[activeIdx] : null;

    function toggle(idx) {
      setActiveIdx((prev) => (prev === idx ? null : idx));
    }

    const fillPct =
      activeIdx !== null && callouts.length > 1
        ? (activeIdx / (callouts.length - 1)) * 100
        : 0;

    return (
      <Section section={section} data-comp={Timeline.displayName}>
        <Container>
          {/* ── Header row ───────────────────────────────────────────────── */}
          <Flex sx={theme.headerRow}>
            <Box sx={theme.headerLeft}>
              {header?.showSuper !== false && header?.super && (
                <Text
                  sx={{
                    ...theme.eyebrow,
                    color: header.superColor || DEFAULT_ACCENT,
                  }}
                >
                  {header.super}
                </Text>
              )}
              {header?.showHeading !== false && header?.heading && (
                <Heading
                  as="h2"
                  sx={{
                    ...theme.heading,
                    color: header.headingColor || '#fff',
                  }}
                >
                  {header.heading}
                </Heading>
              )}
            </Box>

            {header?.cta?.text && (
              <Box sx={{ flexShrink: 0, display: ['none', 'block'] }}>
                {header.ctaStyle === 'lime' ? (
                  <Link
                    href={header.cta.url}
                    newTab={header.cta.newTab}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '42px',
                      px: 11,
                      borderRadius: '22px',
                      bg: 'chartreuseYellow',
                      color: 'text',
                      fontSize: 2,
                      fontWeight: 700,
                      border: '1px solid',
                      borderColor: 'chartreuseYellow',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease-in-out',
                      ':hover': { bg: 'lightGreen', borderColor: 'lightGreen' },
                    }}
                  >
                    {header.cta.text}
                  </Link>
                ) : (
                  <Link
                    href={header.cta.url}
                    newTab={header.cta.newTab}
                    variant={`buttons.${header.ctaStyle || 'primary'}`}
                  >
                    {header.cta.text}
                  </Link>
                )}
              </Box>
            )}
          </Flex>

          {/* ── Desktop layout ────────────────────────────────────────────── */}
          <Box sx={theme.dtWrap}>
            {/* Horizontal rail */}
            <Box sx={{ position: 'relative' }}>
              <Box sx={theme.dtRailBg}>
                <Box
                  sx={{
                    ...theme.dtRailFill,
                    width: `${fillPct}%`,
                    bg:
                      activeCallout?.accentColor ||
                      DEFAULT_ACCENT,
                  }}
                />
                {/* Dots */}
                <Flex sx={theme.dtDotsRow}>
                  {callouts.map((callout, i) => (
                    <Box
                      key={i}
                      onClick={() => toggle(i)}
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '4px',
                        bg:
                          i === activeIdx
                            ? callout.accentColor || DEFAULT_ACCENT
                            : '#333',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                        flexShrink: 0,
                      }}
                    />
                  ))}
                </Flex>
              </Box>

              {/* Year labels */}
              <Flex sx={theme.dtYearsRow}>
                {callouts.map((callout, i) => (
                  <Text
                    key={i}
                    onClick={() => toggle(i)}
                    sx={{
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: i === activeIdx ? 700 : 400,
                      color:
                        i === activeIdx
                          ? callout.accentColor || DEFAULT_ACCENT
                          : '#555',
                      transition: 'color 0.2s',
                      minWidth: '24px',
                      textAlign: 'center',
                    }}
                  >
                    {callout.date}
                  </Text>
                ))}
              </Flex>
            </Box>

            {/* Cards */}
            <Flex sx={theme.dtCards}>
              {callouts.map((callout, i) => {
                const isActive = i === activeIdx;
                const accent = callout.accentColor || DEFAULT_ACCENT;
                return (
                  <Box
                    key={i}
                    onClick={() => toggle(i)}
                    sx={{
                      ...theme.dtCard,
                      borderColor: isActive ? accent : '#2e2e2e',
                    }}
                  >
                    <Text sx={{ ...theme.dtCardYear, color: accent }}>
                      {callout.date}
                    </Text>

                    {callout.image?.src ? (
                      <Box sx={theme.dtCardImgWrap}>
                        <Picture
                          alt={callout.heading}
                          images={[
                            {
                              src: callout.image.src,
                              width: 299,
                              ratio: 299 / 168,
                            },
                          ]}
                        />
                      </Box>
                    ) : (
                      <Box sx={theme.dtCardImgPlaceholder}>
                        <Text sx={{ fontSize: '11px', color: '#555' }}>
                          [ Image ]
                        </Text>
                      </Box>
                    )}

                    <Text
                      sx={{
                        ...theme.dtCardTitle,
                        color: isActive ? accent : '#fff',
                      }}
                    >
                      {callout.heading}
                    </Text>
                  </Box>
                );
              })}
            </Flex>

            {/* Expanded detail panel */}
            {activeCallout && (
              <Box key={activeIdx} sx={theme.dtDetailWrap}>
                <Box sx={theme.dtDetailInner}>
                  {/* Image */}
                  {activeCallout.image?.src ? (
                    <Box sx={theme.dtDetailImgWrap}>
                      <Picture
                        alt={activeCallout.heading}
                        images={[
                          {
                            src: activeCallout.image.src,
                            width: 299,
                            ratio: 299 / 168,
                          },
                        ]}
                      />
                    </Box>
                  ) : (
                    <Box sx={theme.dtDetailImgPlaceholder}>
                      <Text sx={{ fontSize: '14px', color: '#555' }}>
                        [ Campaign photo ]
                      </Text>
                    </Box>
                  )}

                  {/* Copy */}
                  <Box>
                    <Text
                      sx={{
                        ...theme.dtDetailYear,
                        color:
                          activeCallout.accentColor || DEFAULT_ACCENT,
                      }}
                    >
                      {activeCallout.date}
                    </Text>
                    <Heading as="h3" sx={theme.dtDetailTitle}>
                      {activeCallout.heading}
                    </Heading>
                    <Text sx={theme.dtDetailBody}>{activeCallout.copy}</Text>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          {/* ── Mobile layout ─────────────────────────────────────────────── */}
          <Box sx={theme.mbWrap}>

            {/* Year chips */}
            <Box sx={theme.chipsRow}>
              {callouts.map((callout, i) => {
                const isActive = i === activeIdx;
                const accent = callout.accentColor || DEFAULT_ACCENT;
                return (
                  <Box
                    key={i}
                    as="button"
                    type="button"
                    onClick={() => toggle(i)}
                    sx={{
                      ...theme.yrChip,
                      bg: isActive ? accent : 'transparent',
                      borderColor: isActive ? accent : '#333',
                      color: isActive ? '#111' : '#666',
                      fontWeight: isActive ? 700 : 400,
                    }}
                  >
                    {callout.date}
                  </Box>
                );
              })}
            </Box>

            {/* Hint */}
            <Flex sx={theme.hint}>
              <Box
                sx={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  bg:
                    activeCallout?.accentColor || DEFAULT_ACCENT,
                  flexShrink: 0,
                }}
              />
              <Text sx={theme.hintText}>Tap a year to expand its story</Text>
            </Flex>

            {/* Timeline items */}
            <Box>
              {callouts.map((callout, i) => {
                const isActive = i === activeIdx;
                const accent = callout.accentColor || DEFAULT_ACCENT;
                const isLast = i === callouts.length - 1;

                return (
                  <Box key={i} sx={theme.mbItemRow}>
                    {/* Spine */}
                    <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
                      <Box
                        onClick={() => toggle(i)}
                        sx={{
                          width: '40px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          position: 'relative',
                          zIndex: 2,
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: isActive ? '20px' : '12px',
                            height: isActive ? '20px' : '12px',
                            borderRadius: '50%',
                            bg: isActive ? accent : '#2a2a2a',
                            transition: 'all 0.2s',
                          }}
                        />
                      </Box>
                      {!isLast && (
                        <Box
                          sx={{
                            flex: 1,
                            width: '1px',
                            bg: isActive ? accent : '#2a2a2a',
                            transition: 'background 0.3s',
                            minHeight: '20px',
                          }}
                        />
                      )}
                    </Flex>

                    {/* Body */}
                    <Box sx={{ pl: '4px', pb: isLast ? 0 : 0 }}>
                      <Text
                        sx={{
                          ...theme.mbItemYear,
                          color: isActive ? accent : '#555',
                          fontWeight: isActive ? 700 : 400,
                        }}
                      >
                        {callout.date}
                      </Text>
                      <Text
                        as="h3"
                        onClick={() => toggle(i)}
                        sx={{
                          ...theme.mbItemTitle,
                          color: isActive ? accent : '#fff',
                        }}
                      >
                        {callout.heading}
                      </Text>

                      {/* Expanded content */}
                      {isActive && (
                        <Box
                          sx={{
                            animation: 'tlFadeUp 0.3s ease',
                            '@keyframes tlFadeUp': {
                              from: {
                                opacity: 0,
                                transform: 'translateY(6px)',
                              },
                              to: { opacity: 1, transform: 'translateY(0)' },
                            },
                            mb: 6,
                          }}
                        >
                          {callout.image?.src ? (
                            <Box sx={theme.mbExpandImgWrap}>
                              <Picture
                                alt={callout.heading}
                                images={[
                                  {
                                    src: callout.image.src,
                                    width: 299,
                                    ratio: 299 / 168,
                                  },
                                ]}
                              />
                            </Box>
                          ) : (
                            <Box sx={theme.mbExpandImgPlaceholder}>
                              <Text sx={{ fontSize: '13px', color: '#555' }}>
                                [ Campaign photo ]
                              </Text>
                            </Box>
                          )}
                          <Text sx={theme.mbExpandBody}>{callout.copy}</Text>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Mobile CTA — below timeline */}
            {header?.cta?.text && (
              <Box
                sx={{
                  mt: 12,
                  display: 'flex',
                  justifyContent: header.ctaMobileAlign === 'center' ? 'center' : 'flex-start',
                }}
              >
                {header.ctaStyle === 'lime' ? (
                  <Link
                    href={header.cta.url}
                    newTab={header.cta.newTab}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '42px',
                      px: 11,
                      borderRadius: '22px',
                      bg: 'chartreuseYellow',
                      color: 'text',
                      fontSize: 2,
                      fontWeight: 700,
                      border: '1px solid',
                      borderColor: 'chartreuseYellow',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease-in-out',
                      ':hover': { bg: 'lightGreen', borderColor: 'lightGreen' },
                    }}
                  >
                    {header.cta.text}
                  </Link>
                ) : (
                  <Link
                    href={header.cta.url}
                    newTab={header.cta.newTab}
                    variant={`buttons.${header.ctaStyle || 'primary'}`}
                  >
                    {header.cta.text}
                  </Link>
                )}
              </Box>
            )}
          </Box>
        </Container>
      </Section>
    );
  }),
  { triggerOnce: true }
);

Timeline.displayName = 'Timeline';
Timeline.Schema = Schema;
